import { Button, Col, Form, message, Modal, Radio, Row, Select } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import { NON_EMPTY_REGEX } from "common/Constants";
import React, {
  useRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { getClientid, getUserList } from "redux/selectors";
import styled from "styled-components";
import PreviewShift from "./PreviewShift";
import { CALL_API } from "common/API";
import dayjs from "dayjs";

const StyledModal = styled(Modal)`
  .ant-modal-footer {
    display: none;
  }

  .left-panel {
    padding-right: 24px;
  }

  .right-panel {
    padding-left: 16px;
    display: flex;
    gap: 12px;
  }

  .calendar-wrapper {
    flex: 1;
  }

  .calendar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  .calendar-month-title {
    font-weight: 600;
    font-size: 14px;
    color: #1f243b;
  }

  .calendar-nav-btn {
    border: none;
    background: transparent;
    cursor: pointer;
    color: #636980;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .calendar-nav-btn:hover {
    background: #f0f0f0;
  }

  .calendar-grid {
    width: 100%;
    border-collapse: collapse;
    background: #f5f7ff;
    border-radius: 8px;
    overflow: hidden;
  }

  .calendar-grid th {
    text-align: center;
    font-size: 11px;
    color: #636980;
    font-weight: 500;
    padding: 6px 0;
    width: 34px;
  }

  .calendar-grid td {
    text-align: center;
    padding: 0;
    width: 34px;
  }

  .day-checkbox-row td {
    padding: 2px 0 4px 0;
  }

  .day-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .day-checkbox {
    width: 13px;
    height: 13px;
    cursor: pointer;
    accent-color: #2d53da;
  }

  .day-circle {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: #1f243b;
    cursor: pointer;
    margin: 0 auto;
    transition: background 0.15s;
    position: relative;
    z-index: 1;
  }

  .day-circle:hover {
    background: #e3e8fc;
  }

  .day-circle.selected {
    background: #1f2d5c;
    color: #ffffff;
    font-weight: 600;
  }

  .day-circle.applied {
    background: #1f2d5c;
    color: #ffffff;
    font-weight: 600;
  }

  .day-circle.other-month {
    color: #c8ccd8;
    cursor: default;
  }

  .day-circle.other-month:hover {
    background: transparent;
  }

  .day-circle.today {
    color: #2d53da;
    font-weight: 700;
  }

  .day-circle.today.selected {
    color: #ffffff;
  }

  .day-td {
    position: relative;
    padding: 3px 0;
    width: 34px;
  }

  .day-td.range {
    background: #dce4f5;
  }

  .calendar-section-title {
    font-size: 13px;
    color: #1f243b;
    margin-bottom: 10px;
    font-weight: 400;
  }

  .summary-panel {
    width: 150px;
    background: #f5f7ff;
    border-radius: 8px;
    padding: 10px 12px;
    align-self: flex-start;
    max-height: 260px;
    overflow-y: auto;
  }

  .summary-title {
    font-size: 11px;
    font-weight: 600;
    color: #636980;
    margin-bottom: 8px;
    white-space: nowrap;
  }

  .summary-row {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
  }

  .summary-date {
    font-size: 12px;
    color: #1f243b;
    white-space: nowrap;
  }

  .step2-left {
    padding-right: 16px;
    border-right: 1px solid #f0f0f0;
  }

  .step2-right {
    padding-left: 0px;
    background: #f5f7ff;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
  }

  .applied-info-box {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 12px;
    font-size: 13px;
  }

  .applied-info-title {
    font-weight: 600;
    color: #1f243b;
    margin-bottom: 4px;
  }

  .applied-info-sub {
    color: #636980;
    font-size: 12px;
  }

  .preview-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: #636980;
    font-size: 13px;
  }

  .folder-icon {
    font-size: 48px;
    color: #f5a623;
  }

  .footer-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
    padding-top: 14px;
    border-top: 1px solid #f0f0f0;
  }

  .cancel-btn {
    width: 100px;
    height: 36px;
    border-radius: 8px;
    border: 1px solid #d0d0d0;
    color: #1f243b;
    font-size: 14px;
    background: #fff;
  }

  .prev-btn {
    width: 100px;
    height: 36px;
    border-radius: 8px;
    border: 1px solid #2d53da;
    color: #2d53da;
    font-size: 14px;
    background: #fff;
  }

  .next-btn {
    width: 100px;
    height: 36px;
    border-radius: 8px;
    background-color: #2d53da;
    color: #ffffff;
    font-size: 14px;
    border: none;
  }

  .next-btn:hover {
    background-color: #1e3eb5 !important;
    color: #ffffff !important;
  }

  .complete-btn {
    width: 110px;
    height: 36px;
    border-radius: 8px;
    background-color: #2d53da;
    color: #ffffff;
    font-size: 14px;
    border: none;
  }

  .complete-btn:hover {
    background-color: #1e3eb5 !important;
    color: #ffffff !important;
  }

  .calendar-grid tbody tr:hover .day-td {
    background: #dce4f5;
  }

  .calendar-grid tbody tr:hover .day-circle {
    background: transparent;
  }

  .calendar-grid tbody tr:hover .day-circle.selected,
  .calendar-grid tbody tr:hover .day-circle.applied {
    background: #1f2d5c;
    color: #fff;
  }

  .day-circle.disabled {
    color: #c8ccd8;
    cursor: not-allowed;
    pointer-events: none;
  }

  .day-circle.disabled:hover {
    background: transparent;
  }
`;

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const CalendarGrid = ({
  year,
  month,
  selectedSet,
  appliedSet,
  onDayClick,
  showCheckboxes,
  onColCheckbox,
  disabledSet,
}) => {
  const today = new Date();

  const makeKey = (y, m, d) => `${y}-${m}-${d}`;

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const prevMonthDays = new Date(year, month, 0).getDate();

  const cells = [];

  for (let i = 0; i < firstDay; i++) {
    cells.push({
      day: prevMonthDays - firstDay + 1 + i,
      isOtherMonth: true,
      year: month === 0 ? year - 1 : year,
      month: month === 0 ? 11 : month - 1,
    });
  }

  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({
      day: d,
      isOtherMonth: false,
      year,
      month,
    });
  }

  const remaining = 42 - cells.length;

  for (let i = 1; i <= remaining; i++) {
    cells.push({
      day: i,
      isOtherMonth: true,
      year: month === 11 ? year + 1 : year,
      month: month === 11 ? 0 : month + 1,
    });
  }

  const rows = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7));
  }

  const isToday = (d, otherMonth) =>
    !otherMonth &&
    d === today.getDate() &&
    month === today.getMonth() &&
    year === today.getFullYear();

  let rangeStart = null;
  let rangeEnd = null;

  if (selectedSet && selectedSet.size > 0) {
    const dates = [...selectedSet].map((k) => {
      const [y, m, d] = k.split("-").map(Number);
      return new Date(y, m, d);
    });
    rangeStart = new Date(Math.min(...dates));
    rangeEnd = new Date(Math.max(...dates));
  } else if (appliedSet && appliedSet.size > 0) {
    const dates = [...appliedSet].map((k) => {
      const [y, m, d] = k.split("-").map(Number);
      return new Date(y, m, d);
    });
    rangeStart = new Date(Math.min(...dates));
    rangeEnd = new Date(Math.max(...dates));
  }

  const isColChecked = (colIdx) =>
    rows.every((row) => {
      const c = row[colIdx];
      if (!c || c.isOtherMonth) return true;
      return selectedSet.has(makeKey(c.year, c.month, c.day));
    });

  return (
    <table className="calendar-grid">
      <thead>
        <tr>
          {DAYS.map((d) => (
            <th key={d}>{d}</th>
          ))}
        </tr>

        {showCheckboxes && (
          <tr className="day-checkbox-row">
            {DAYS.map((d, colIdx) => (
              <td key={d}>
                <div className="day-cell">
                  <input
                    type="checkbox"
                    className="day-checkbox"
                    checked={isColChecked(colIdx)}
                    onChange={(e) =>
                      onColCheckbox &&
                      onColCheckbox(colIdx, e.target.checked, rows)
                    }
                  />
                </div>
              </td>
            ))}
          </tr>
        )}
      </thead>

      <tbody>
        {rows.map((row, rowIdx) => (
          <tr key={rowIdx}>
            {row.map((cell, colIdx) => {
              const key = makeKey(cell.year, cell.month, cell.day);

              const isSelected = !cell.isOtherMonth && selectedSet.has(key);
              const isApplied = !cell.isOtherMonth && appliedSet?.has(key);
              const isDisabled =
                !cell.isOtherMonth && disabledSet?.has(key) === true;

              const currentDate = new Date(cell.year, cell.month, cell.day);

              const inRange =
                rangeStart &&
                rangeEnd &&
                currentDate >= rangeStart &&
                currentDate <= rangeEnd &&
                !cell.isOtherMonth;

              let tdClass = "day-td";
              if (inRange) tdClass += " range";

              return (
                <td key={colIdx} className={tdClass}>
                  <div
                    className={`day-circle
                      ${cell.isOtherMonth ? "other-month" : ""}
                      ${isSelected ? "selected" : ""}
                      ${isApplied && !isSelected ? "applied" : ""}
                      ${isToday(cell.day, cell.isOtherMonth) ? "today" : ""}
                      ${isDisabled ? "disabled" : ""}
                    `}
                    onClick={() => {
                      if (!cell.isOtherMonth && !isDisabled && onDayClick) {
                        onDayClick(cell);
                      }
                    }}
                  >
                    {cell.day}
                  </div>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const CloneShift = ({ open, setOpen }) => {
  const ClientId = useSelector(getClientid);
  const userList = useSelector(getUserList);
  const [form] = Form.useForm();
  const containerRef = useRef(null);
  const today = new Date();

  const [step, setStep] = useState(1);
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [selectedDays, setSelectedDays] = useState({});
  const [cloningMethod, setCloningMethod] = useState(null);

  const [step2Year, setStep2Year] = useState(today.getFullYear());
  const [step2Month, setStep2Month] = useState(today.getMonth());
  const [appliedDays, setAppliedDays] = useState({});
  const [applyCount, setApplyCount] = useState(0);
  const [baseAssignedShiftList, setBaseAssignedShiftList] = useState([]);
  const [assignedShiftList, setAssignedShiftList] = useState([]);
  console.log("assignedShiftList", assignedShiftList);

  const referenceEmployee = Form.useWatch("referenceemployee", form);

  const makeKey = (year, month, day) => `${year}-${month}-${day}`;

  const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const selectedSet = useMemo(
    () =>
      new Set(
        Object.entries(selectedDays)
          .filter(([, v]) => v)
          .map(([k]) => k)
      ),
    [selectedDays]
  );

  const sortedSelected = useMemo(
    () =>
      [...selectedSet]
        .map((key) => {
          const [y, m, d] = key.split("-").map(Number);
          return { year: y, month: m, day: d, date: new Date(y, m, d) };
        })
        .sort((a, b) => a.date - b.date),
    [selectedSet]
  );

  const summaryDates = useMemo(
    () =>
      sortedSelected.map(({ year, month, day }) =>
        new Date(year, month, day).toLocaleDateString("default", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      ),
    [sortedSelected]
  );

  const selectedDateKeys = useMemo(
    () => sortedSelected.map(({ date }) => formatLocalDate(date)),
    [sortedSelected]
  );

  const appliedSet = useMemo(
    () =>
      new Set(
        Object.entries(appliedDays)
          .filter(([, v]) => v)
          .map(([k]) => k)
      ),
    [appliedDays]
  );

  const offsets = useMemo(() => {
    if (sortedSelected.length === 0) return [];
    const base = sortedSelected[0].date;
    return sortedSelected.map(({ date }) =>
      Math.round((date - base) / 86400000)
    );
  }, [sortedSelected]);

  const mergeUserShifts = useCallback((data) => {
    const userMap = new Map();

    data.forEach((item) => {
      if (!item?.userId) return;

      if (!userMap.has(item.userId)) {
        userMap.set(item.userId, {
          ...item,
          dates: [...(item.dates || [])],
        });
      } else {
        const existing = userMap.get(item.userId);
        const mergedDates = [...(existing.dates || []), ...(item.dates || [])];

        existing.dates = [
          ...new Map(mergedDates.map((d) => [d.date, d])).values(),
        ];
      }
    });

    return Array.from(userMap.values());
  }, []);

  const filterDates = useCallback(
    (data) => {
      return data
        .map((user) => ({
          ...user,
          dates: (user.dates || []).filter((d) =>
            selectedDateKeys.includes(d.date)
          ),
        }))
        .filter((user) => user.dates.length > 0);
    },
    [selectedDateKeys]
  );

  const getAssignedShiftList = useCallback(async () => {
    if (!sortedSelected?.length) return;

    try {
      const URL =
        cloningMethod === "single"
          ? `get-user-shift/${ClientId}/${referenceEmployee}`
          : `get-assigned-shift/${ClientId}`;

      const { data } = await CALL_API(URL, "post", {
        from: formatLocalDate(sortedSelected[0].date),
        to: formatLocalDate(sortedSelected[sortedSelected.length - 1].date),
      });

      const mergedData = mergeUserShifts(Array.isArray(data) ? data : []);

      const filteredData = filterDates(mergedData);

      if (cloningMethod === "single") {
        const employees = form.getFieldValue("employeelist") || [];

        if (!data?.length) {
          setAssignedShiftList([]);
          return;
        }

        /* ✅ Step 1: Merge all entries */
        const mergedData = mergeUserShifts(data);
        console.log(mergedData, "mergeUserShifts");

        /* ✅ Step 2: Get reference employee data BEFORE filtering */
        const referenceEmployee = form.getFieldValue("referenceemployee");

        const referenceUserData = mergedData.find(
          (u) => u.userId === referenceEmployee
        );
        console.log("referenceUserData", referenceUserData);
        if (!referenceUserData) {
          setAssignedShiftList([]);
          return;
        }

        /* ✅ Step 3: Filter only that user's dates */
        const formattedDates = summaryDates.map((d) =>
          dayjs(d).format("YYYY-MM-DD")
        );
        console.log("summaryDates1", summaryDates);
        console.log("formattedDates", formattedDates);

        const filteredDates = (referenceUserData.dates || []).filter((d) =>
          formattedDates.includes(d.date)
        );
        console.log("filteredDates", filteredDates);

        if (!filteredDates.length) {
          setAssignedShiftList([]);
          return;
        }

        /* ✅ Step 4: Clone to selected employees */
        const clonedShiftList = employees.map((empId) => ({
          ...referenceUserData,
          userId: empId,
          dates: filteredDates,
        }));
        console.log("clonedShiftList", clonedShiftList);

        setBaseAssignedShiftList(clonedShiftList);
        setAssignedShiftList(clonedShiftList);
      } else {
        setBaseAssignedShiftList(filteredData);
        setAssignedShiftList(filteredData);
      }
    } catch (error) {
      console.log("[getAssignedShiftList] Error:", error);
    }
  }, [
    ClientId,
    filterDates,
    cloningMethod,
    form?.getFieldValue("employeelist"),
    mergeUserShifts,
    referenceEmployee,
    sortedSelected,
  ]);

  useEffect(() => {
    if (sortedSelected.length > 0 && cloningMethod) {
      getAssignedShiftList();
    }
  }, [
    sortedSelected.length,
    cloningMethod,
    form?.getFieldValue("employeelist"),
    form.getFieldValue("referenceemployee"),
  ]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const toggleDay = (cell) => {
    const key = makeKey(cell.year, cell.month, cell.day);
    setSelectedDays((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleColCheckbox = (colIdx, checked, rows) => {
    const updates = {};

    rows.forEach((row) => {
      const cell = row[colIdx];
      if (cell && !cell.isOtherMonth) {
        updates[makeKey(cell.year, cell.month, cell.day)] = checked;
      }
    });

    setSelectedDays((prev) => ({ ...prev, ...updates }));
  };

  const step2MonthName = new Date(step2Year, step2Month).toLocaleString(
    "default",
    { month: "long" }
  );

  const handleStep2PrevMonth = () => {
    if (step2Month === 0) {
      setStep2Month(11);
      setStep2Year((y) => y - 1);
    } else {
      setStep2Month((m) => m - 1);
    }
  };

  const handleStep2NextMonth = () => {
    if (step2Month === 11) {
      setStep2Month(0);
      setStep2Year((y) => y + 1);
    } else {
      setStep2Month((m) => m + 1);
    }
  };

  const handleStep2DayClick = (cell) => {
    if (
      !offsets.length ||
      !sortedSelected.length
      // ||
      // !baseAssignedShiftList.length
    ) {
      return;
    }

    const anchorDate = new Date(cell.year, cell.month, cell.day);
    const newApplied = { ...appliedDays };

    offsets.forEach((offset) => {
      const d = new Date(anchorDate);
      d.setDate(anchorDate.getDate() + offset);

      const key = makeKey(d.getFullYear(), d.getMonth(), d.getDate());
      newApplied[key] = true;
    });

    setAppliedDays(newApplied);

    const baseSelectedDate = new Date(sortedSelected[0].date);
    baseSelectedDate.setHours(0, 0, 0, 0);

    const newClonedShiftList = baseAssignedShiftList.map((item) => {
      const updatedDates = (item.dates || []).map((shift) => {
        const originalShiftDate = new Date(shift.date);
        originalShiftDate.setHours(0, 0, 0, 0);

        const offsetDays = Math.round(
          (originalShiftDate - baseSelectedDate) / 86400000
        );

        const newDate = new Date(anchorDate);
        newDate.setDate(anchorDate.getDate() + offsetDays);

        return {
          ...shift,
          date: formatLocalDate(newDate),
        };
      });

      return {
        ...item,
        dates: updatedDates,
        startdate: updatedDates[0]?.date,
        enddate: updatedDates[updatedDates.length - 1]?.date,
      };
    });

    setAssignedShiftList((prev) => {
      if (applyCount === 0) {
        return [...newClonedShiftList];
      }
      return [...prev, ...newClonedShiftList];
    });

    setApplyCount((prev) => prev + 1);
  };

  const step2DisabledSet = useMemo(() => {
    const set = new Set();
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);

    const daysInMonth = new Date(step2Year, step2Month + 1, 0).getDate();

    for (let d = 1; d <= daysInMonth; d++) {
      const cellDate = new Date(step2Year, step2Month, d);
      if (cellDate < todayMidnight) {
        set.add(makeKey(step2Year, step2Month, d));
      }
    }

    appliedSet.forEach((key) => set.add(key));

    return set;
  }, [step2Year, step2Month, appliedSet]);

  const acrossMonths = useMemo(() => {
    const months = new Set(
      [...appliedSet].map((key) => {
        const [y, m] = key.split("-");
        return `${y}-${m}`;
      })
    );
    return months.size;
  }, [appliedSet]);

  const handleReset = () => {
    setOpen(false);
    form.resetFields();
    setSelectedDays({});
    setCloningMethod(null);
    setStep(1);
    setAppliedDays({});
    setApplyCount(0);
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
    setStep2Year(today.getFullYear());
    setStep2Month(today.getMonth());
    setBaseAssignedShiftList([]);
    setAssignedShiftList([]);
  };

  const handleNext = async () => {
    try {
      await form.validateFields();

      if (selectedSet.size === 0) return;

      // ✅ Reset step 2 data before moving again
      setAppliedDays({});
      setApplyCount(0);
      setAssignedShiftList([]);
      // setBaseAssignedShiftList([]);

      setStep(2);
      setStep2Year(today.getFullYear());
      setStep2Month(today.getMonth());
    } catch (error) {
      // validation failed
    }
  };

  const buildUserDateArray = () => {
    const arr = [];

    assignedShiftList?.forEach((item) => {
      item?.dates?.forEach((d) => {
        arr.push({
          userid: item.userId,
          colKey: d.date,
          shiftid: d.shift_id,
          shifttitle: d.shift_title,
          color: d.color,
        });
      });
    });

    return arr;
  };

  const handleComplete = async () => {
    try {
      const userDateArray = buildUserDateArray();

      if (!userDateArray.length) {
        message.error("No shift data available to assign");
        return;
      }
      console.log("userDateArray", userDateArray);
      const payload = {
        clientId: ClientId,
        userId: form.getFieldValue("employeelist"),
        from: userDateArray[0]?.colKey,
        to: userDateArray[userDateArray.length - 1]?.colKey,
        shift: userDateArray[0]?.shiftid,
        replaceExisting: true,
        notifyByEmail: false,
        color: userDateArray[0]?.color,
        userDateArray,
        method: "assign_shift",
        localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const response = await CALL_API(
        `assign-shift/${ClientId}`,
        "post",
        payload
      );

      if (
        response?.message === "Shift assigned successfully." ||
        response?.message === "Shift assignment completed."
      ) {
        message.success("Shift cloned successfully");
        handleReset();
      } else {
        message.error("Something went wrong");
      }
    } catch (error) {
      console.log("[CloneShift Complete] Error:", error);
    }
  };

  const groupedSummaryDates = useMemo(() => {
    const map = {};

    sortedSelected.forEach(({ year, month, day }) => {
      const monthKey = new Date(year, month).toLocaleString("default", {
        month: "short",
        year: "numeric",
      });

      if (!map[monthKey]) {
        map[monthKey] = [];
      }

      map[monthKey].push(day);
    });

    return map;
  }, [sortedSelected]);

  const appliedMonths = useMemo(() => {
    const map = {};

    [...appliedSet].forEach((key) => {
      const [y, m] = key.split("-").map(Number);

      const monthLabel = new Date(y, m).toLocaleString("default", {
        month: "short",
        year: "numeric",
      });

      map[monthLabel] = true;
    });

    return Object.keys(map);
  }, [appliedSet]);

  return (
    <StyledModal
      title="Clone Shift"
      open={open}
      onCancel={handleReset}
      width={900}
      footer={null}
    >
      {step === 1 && (
        <>
          <Row>
            <Col
              xs={24}
              sm={cloningMethod === "all" || referenceEmployee ? 12 : 24}
              className="left-panel"
            >
              <div ref={containerRef}>
                <Form
                  form={form}
                  name="cloneShift"
                  layout="vertical"
                  onValuesChange={(changed) => {
                    if ("cloningmethod" in changed) {
                      setCloningMethod(changed.cloningmethod);
                    }
                  }}
                >
                  <Form.Item
                    label="Select employees to clone shift for"
                    name="employeelist"
                    rules={[
                      {
                        required: true,
                        pattern: NON_EMPTY_REGEX,
                        message: "Selected employees is required",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder="Select employees"
                      mode="multiple"
                      maxTagCount="responsive"
                      options={userList?.filter((u) => u.statusid === "1")}
                      fieldNames={{ label: "fullname", value: "userid" }}
                      getPopupContainer={() => containerRef.current}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Select Cloning Method"
                    name="cloningmethod"
                    rules={[
                      { required: true, message: "Cloning Method is required" },
                    ]}
                    onChange={() => {
                      setSelectedDays({});
                      form.setFieldValue("referenceemployee", null);
                      setAssignedShiftList([]);
                      setBaseAssignedShiftList([]);
                    }}
                  >
                    <Radio.Group
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <Radio value="all">
                        Clone each employee's shift individually
                      </Radio>
                      <Radio value="single">
                        Apply one reference employee's shift to others
                      </Radio>
                    </Radio.Group>
                  </Form.Item>

                  {cloningMethod === "single" && (
                    <Form.Item
                      label="Choose Reference employee"
                      name="referenceemployee"
                      rules={[
                        {
                          required: true,
                          pattern: NON_EMPTY_REGEX,
                          message: "Reference employee is required",
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder="Select employee"
                        maxTagCount="responsive"
                        options={userList?.filter((u) => u.statusid === "1")}
                        fieldNames={{ label: "fullname", value: "userid" }}
                        getPopupContainer={() => containerRef.current}
                      />
                    </Form.Item>
                  )}
                </Form>
              </div>
            </Col>

            {(cloningMethod === "all" || referenceEmployee) && (
              <Col xs={24} sm={12} className="right-panel">
                <div style={{ width: "100%" }}>
                  <p className="calendar-section-title">
                    Select the days whose shifts you want to clone
                  </p>

                  <div style={{ display: "flex", gap: 12 }}>
                    <div className="calendar-wrapper">
                      <div className="calendar-header">
                        <button
                          type="button"
                          className="calendar-nav-btn"
                          onClick={handlePrevMonth}
                        >
                          <LeftOutlined />
                        </button>
                        <span className="calendar-month-title">
                          {new Date(currentYear, currentMonth).toLocaleString(
                            "default",
                            { month: "long" }
                          )}
                          , {currentYear}
                        </span>
                        <button
                          type="button"
                          className="calendar-nav-btn"
                          onClick={handleNextMonth}
                        >
                          <RightOutlined />
                        </button>
                      </div>

                      <CalendarGrid
                        year={currentYear}
                        month={currentMonth}
                        selectedSet={selectedSet}
                        appliedSet={new Set()}
                        onDayClick={toggleDay}
                        showCheckboxes={true}
                        onColCheckbox={handleColCheckbox}
                      />
                    </div>

                    {summaryDates.length > 0 && (
                      <div className="summary-panel">
                        <div className="summary-title">
                          Days Chosen to clone
                        </div>

                        {Object.entries(groupedSummaryDates).map(
                          ([month, days]) => (
                            <div
                              className="d-flex justify-content-between align-items-center"
                              key={month}
                            >
                              <span className="summary-date">{month}</span>

                              <span className="summary-count">
                                {days.length}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            )}
          </Row>

          <div className="footer-buttons">
            <Button className="cancel-btn" onClick={handleReset}>
              Cancel
            </Button>
            <Button
              className="next-btn"
              onClick={handleNext}
              disabled={selectedSet.size === 0}
            >
              Next
            </Button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <Row>
            <Col xs={24} sm={12} className="step2-left">
              <p className="calendar-section-title">
                Apply the selected day(s) using the calendar below
              </p>

              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div className="calendar-wrapper">
                  <div className="calendar-header">
                    <button
                      type="button"
                      className="calendar-nav-btn"
                      onClick={handleStep2PrevMonth}
                    >
                      <LeftOutlined />
                    </button>
                    <span className="calendar-month-title">
                      {step2MonthName}, {step2Year}
                    </span>
                    <button
                      type="button"
                      className="calendar-nav-btn"
                      onClick={handleStep2NextMonth}
                    >
                      <RightOutlined />
                    </button>
                  </div>

                  <CalendarGrid
                    year={step2Year}
                    month={step2Month}
                    selectedSet={new Set()}
                    appliedSet={appliedSet}
                    onDayClick={handleStep2DayClick}
                    showCheckboxes={false}
                    disabledSet={step2DisabledSet}
                  />
                </div>

                <div style={{ minWidth: 120 }}>
                  <div className="applied-info-box">
                    <div className="applied-info-title">
                      Applied ({applyCount}) time{applyCount !== 1 ? "s" : ""}
                    </div>

                    <div className="applied-info-sub">
                      {appliedMonths.length > 1
                        ? "Across Months"
                        : appliedMonths.length === 1
                        ? "Same Month"
                        : ""}
                    </div>

                    {/* ✅ SHOW MONTHS */}
                    {appliedMonths.length > 0 && (
                      <div
                        style={{ marginTop: 6, fontSize: 12, color: "#1f243b" }}
                      >
                        {appliedMonths.map((m) => (
                          <div key={m}>{m}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Col>

            <Col xs={24} sm={12}>
              <div className="step2-right">
                {appliedSet.size === 0 ? (
                  <div className="preview-empty">
                    <FolderOpenOutlined
                      className="folder-icon"
                      style={{ fontSize: 52, color: "#f5a623" }}
                    />
                    <span>Apply Selected Days to preview shifts</span>
                  </div>
                ) : (
                  <PreviewShift
                    userList={userList?.filter((item) =>
                      form.getFieldValue("employeelist")?.includes(item?.userid)
                    )}
                    assignedShiftList={assignedShiftList}
                  />
                )}
              </div>
            </Col>
          </Row>

          <div className="footer-buttons">
            <Button className="cancel-btn" onClick={handleReset}>
              Cancel
            </Button>
            <Button
              className="prev-btn"
              onClick={() => {
                setStep(1);
                // setAppliedDays({});
                // setApplyCount(0);
                // setAssignedShiftList([]);
              }}
            >
              Previous
            </Button>
            <Button
              className="complete-btn"
              disabled={appliedSet.size === 0}
              onClick={handleComplete}
            >
              Complete
            </Button>
          </div>
        </>
      )}
    </StyledModal>
  );
};

export default CloneShift;
